/**
 * ValueInput component for entering parameter values in the FAB data entry modal.
 *
 * Features:
 * - +/- buttons for adjusting values with parameter-specific steps
 * - Target range display with status badges (In range, Below target, Above target)
 * - Parameter-specific decimal places for display
 */
import { Stack, Group, Text, ActionIcon, NumberInput, Badge, rem } from '@mantine/core';
import { IconChevronUp, IconChevronDown, IconCheck } from '@tabler/icons-react';
import type { ParameterTarget } from '@/api/measurements';

/**
 * Minimal parameter config interface for ValueInput.
 * Works with both ParameterInfo and ManualEntryParameterInfo types.
 */
interface ParamConfig {
  key: string;
  display_name: string;
  unit: string;
}

/**
 * Step values for +/- buttons, per parameter type.
 * Keys must match the parameters table (ca, mg, no3, po4).
 * Defaults to 1 if not specified.
 */
const STEP_VALUES: Record<string, number> = {
  ph: 0.01,
  salinity: 0.1,
  alkalinity: 0.1,
  ca: 1,
  mg: 10,
  no3: 0.1,
  po4: 0.01,
};

/**
 * Reasonable bounds for parameter values.
 * Used to clamp values when adjusting with +/- buttons.
 * Keys must match the parameters table (ca, mg, no3, po4).
 */
const PARAMETER_BOUNDS: Record<string, { min: number; max: number }> = {
  ph: { min: 0, max: 14 },
  salinity: { min: 0, max: 50 },
  alkalinity: { min: 0, max: 20 },
  ca: { min: 0, max: 1000 },
  mg: { min: 0, max: 2000 },
  no3: { min: 0, max: 200 },
  po4: { min: 0, max: 5 },
};

/**
 * Decimal places for display and rounding, per parameter type.
 * Keys must match the parameters table (ca, mg, no3, po4).
 * Defaults to 2 if not specified.
 */
const DECIMAL_PLACES: Record<string, number> = {
  ph: 2,
  salinity: 1,
  alkalinity: 1,
  ca: 0,
  mg: 0,
  no3: 1,
  po4: 2,
};

export interface ValueInputProps {
  /** Current value or empty string if not set */
  value: number | '';
  /** Callback when value changes */
  onChange: (value: number | '') => void;
  /** Parameter configuration with display name, unit, etc. */
  paramConfig: ParamConfig;
  /** Target range for the parameter, or null if not set */
  targetRange: ParameterTarget | null;
  /** Optional ref for the number input */
  inputRef?: React.RefObject<HTMLInputElement>;
}

/**
 * Get the step value for a parameter.
 */
function getStepValue(paramKey: string): number {
  return STEP_VALUES[paramKey] ?? 1;
}

/**
 * Get the bounds for a parameter.
 * Returns null if no bounds are defined.
 */
function getParameterBounds(paramKey: string): { min: number; max: number } | null {
  return PARAMETER_BOUNDS[paramKey] ?? null;
}

/**
 * Clamp a value to the parameter's bounds.
 */
function clampToBounds(value: number, paramKey: string): number {
  const bounds = getParameterBounds(paramKey);
  if (!bounds) return value;
  return Math.max(bounds.min, Math.min(bounds.max, value));
}

/**
 * Get the number of decimal places for a parameter.
 */
function getDecimalPlaces(paramKey: string): number {
  return DECIMAL_PLACES[paramKey] ?? 2;
}

/**
 * Round a value to the appropriate decimal places for the parameter.
 */
function roundValue(value: number, paramKey: string): number {
  const decimalPlaces = getDecimalPlaces(paramKey);
  const factor = Math.pow(10, decimalPlaces);
  return Math.round(value * factor) / factor;
}

/**
 * Determine the range status based on value and target range.
 * Returns 'ok', 'low', 'high', or null if no target range or no value.
 */
function getRangeStatus(
  value: number | '',
  targetRange: ParameterTarget | null
): 'ok' | 'low' | 'high' | null {
  if (value === '' || typeof value !== 'number') return null;
  if (!targetRange) return null;
  if (targetRange.low === null || targetRange.high === null) return null;

  if (value < targetRange.low) return 'low';
  if (value > targetRange.high) return 'high';
  return 'ok';
}

/**
 * Get the badge configuration for a range status.
 */
function getStatusBadge(
  status: 'ok' | 'low' | 'high' | null
): { text: string; color: string; icon?: React.ReactNode } | null {
  switch (status) {
    case 'ok':
      return {
        text: 'In range',
        color: 'green',
        icon: <IconCheck size={14} />,
      };
    case 'low':
      return { text: 'Below target', color: 'orange' };
    case 'high':
      return { text: 'Above target', color: 'red' };
    default:
      return null;
  }
}

export function ValueInput({
  value,
  onChange,
  paramConfig,
  targetRange,
  inputRef,
}: ValueInputProps) {
  const step = getStepValue(paramConfig.key);
  const decimalPlaces = getDecimalPlaces(paramConfig.key);

  /**
   * Adjust the value by the step amount, clamped to parameter bounds.
   */
  function adjustValue(direction: 'up' | 'down') {
    const currentValue = typeof value === 'number' ? value : 0;
    const delta = direction === 'up' ? step : -step;
    const rawValue = roundValue(currentValue + delta, paramConfig.key);
    const clampedValue = clampToBounds(rawValue, paramConfig.key);
    onChange(clampedValue);
  }

  const rangeStatus = getRangeStatus(value, targetRange);
  const statusBadge = getStatusBadge(rangeStatus);

  // Format unit display
  const unitDisplay = paramConfig.unit ? ` ${paramConfig.unit}` : '';

  return (
    <Stack gap="xs">
      {/* Parameter name */}
      <Text fw={500} size="sm">
        {paramConfig.display_name}
        {unitDisplay && <Text span c="dimmed">{unitDisplay}</Text>}
      </Text>

      {/* Value input with +/- buttons */}
      <Group gap="xs" justify="center">
        <ActionIcon
          variant="light"
          size="lg"
          radius="xl"
          onClick={() => adjustValue('down')}
          aria-label="Decrease value"
        >
          <IconChevronDown style={{ width: rem(20), height: rem(20) }} />
        </ActionIcon>

        <NumberInput
          value={value}
          onChange={(val) => {
            if (typeof val === 'number') {
              onChange(val);
            } else {
              onChange('');
            }
          }}
          decimalScale={decimalPlaces}
          step={step}
          hideControls
          styles={{
            input: {
              textAlign: 'center',
              width: rem(100),
              fontSize: rem(18),
              fontWeight: 500,
            },
          }}
          ref={inputRef}
        />

        <ActionIcon
          variant="light"
          size="lg"
          radius="xl"
          onClick={() => adjustValue('up')}
          aria-label="Increase value"
        >
          <IconChevronUp style={{ width: rem(20), height: rem(20) }} />
        </ActionIcon>
      </Group>

      {/* Target range and status */}
      {targetRange && targetRange.low !== null && targetRange.high !== null && (
        <Group gap="xs" justify="center">
          <Text size="xs" c="dimmed">
            Target: {targetRange.low} - {targetRange.high}
          </Text>
          {statusBadge && (
            <Badge
              size="sm"
              color={statusBadge.color}
              leftSection={statusBadge.icon}
            >
              {statusBadge.text}
            </Badge>
          )}
        </Group>
      )}
    </Stack>
  );
}
